import React, { useState, useEffect } from 'react';
import { BlockAQuestion, CampaignQuestion, BlockAAnswer, CampaignAnswer } from '../types';

interface QuestionCardProps {
  question: BlockAQuestion | CampaignQuestion;
  onAnswer: (answer: BlockAAnswer | CampaignAnswer) => void;
  onCashAwarded?: (amount: number) => void;
  disabled?: boolean;
  campaignId?: string;
}

const QuestionCard: React.FC<QuestionCardProps> = ({
  question,
  onAnswer,
  onCashAwarded,
  disabled = false,
  campaignId
}) => {
  const [selectedAnswer, setSelectedAnswer] = useState<string | number | null>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [textAnswer, setTextAnswer] = useState('');
  const [isAnimating, setIsAnimating] = useState(false);

  // Block A questions are curriculum questions (emoji rating or multiple choice from blockA template)
  // Block B questions are campaign questions (have campaignId prop passed to component)
  const isBlockA = !campaignId; // If no campaignId prop, it's a Block A curriculum question
  const isCampaign = !!campaignId; // If campaignId prop exists, it's a Block B campaign question
  
  // Debug logging removed for production performance

  useEffect(() => {
    // Reset state when question changes
    setSelectedAnswer(null);
    setIsSubmitted(false);
    setTextAnswer('');
    setIsAnimating(false);
  }, [question.id]);

  const handleMultipleChoiceSelect = (optionIndex: number, optionText: string) => {
    if (disabled || isSubmitted) return;
    
    setSelectedAnswer(optionIndex);
    playSelectionSound();
    
    // Submit immediately - visual feedback will continue
    submitAnswer(optionIndex, optionText);
  };

  const handleEmojiSelect = (value: number, emoji: string) => {
    if (disabled || isSubmitted) return;
    
    setSelectedAnswer(value);
    playSelectionSound();
    
    // Submit immediately - visual feedback will continue
    submitAnswer(value, emoji);
  };

  const handleTextSubmit = () => {
    if (disabled || isSubmitted || !textAnswer.trim()) return;
    
    setSelectedAnswer(textAnswer);
    submitAnswer(textAnswer, textAnswer);
  };

  const submitAnswer = (answerValue: string | number, displayValue: string) => {
    setIsSubmitted(true);
    setIsAnimating(true);
    
    // If multiple choice with correctness, show right/wrong feedback
    let isCorrect: boolean | null = null;
    if (isBlockA && (question as BlockAQuestion).type === 'multiple_choice') {
      const correctIndex = (question as BlockAQuestion).correctIndex;
      if (typeof correctIndex === 'number') {
        isCorrect = answerValue === correctIndex;
      }
    }
    
    // Play sound depending on correctness (or generic success if not graded)
    if (isCorrect === false) {
      playErrorSound();
    } else {
      playSuccessSound();
    }
    
    // Cash animation handled by parent component
    
    // Call cash awarded callback (award even if wrong? choose to award only if correct when graded)
    if (isCorrect === false) {
      // no-op: no cash for wrong graded answers
    } else {
      onCashAwarded?.(20);
    }
    
    let answer: BlockAAnswer | CampaignAnswer;
    
    if (isBlockA) {
      const blockAQ = question as BlockAQuestion;
      if (blockAQ.type === 'multiple_choice') {
        answer = {
          questionId: question.id,
          choiceId: displayValue
        };
      } else {
        answer = {
          questionId: question.id,
          emojiValue: answerValue as number
        };
      }
      
      // Ensure answer payload does not contain undefined values
    } else {
      if (question.type === 'multiple_choice' || question.type === 'text') {
        answer = {
          campaignId: campaignId!,
          questionId: question.id,
          type: question.type,
          answer: displayValue
        };
      } else {
        // This shouldn't happen for campaign questions, but let's handle it
        answer = {
          campaignId: campaignId!,
          questionId: question.id,
          type: 'text' as const,
          answer: displayValue
        };
      }
    }
    
    // Call answer callback immediately - animations will continue independently
    onAnswer(answer);
  };

  const playSelectionSound = () => {
    try {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.frequency.setValueAtTime(880, audioContext.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(1100, audioContext.currentTime + 0.1);
      oscillator.type = 'sine';

      gainNode.gain.setValueAtTime(0, audioContext.currentTime);
      gainNode.gain.linearRampToValueAtTime(0.03, audioContext.currentTime + 0.01);
      gainNode.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + 0.2);

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      const now = audioContext.currentTime;
      oscillator.start(now);
      oscillator.stop(now + 0.2);
    } catch {}
  };

  const playSuccessSound = () => {
    try {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      
      // Create a triumphant success chord
      const oscillators = [
        { freq: 523.25, type: 'sine' as OscillatorType }, // C5
        { freq: 659.25, type: 'sine' as OscillatorType }, // E5
        { freq: 783.99, type: 'sine' as OscillatorType }, // G5
      ];
      
      oscillators.forEach((osc, i) => {
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        const filter = audioContext.createBiquadFilter();
        
        oscillator.frequency.setValueAtTime(osc.freq, audioContext.currentTime);
        oscillator.type = osc.type;
        
        filter.type = 'lowpass';
        filter.frequency.setValueAtTime(2000, audioContext.currentTime);
        filter.Q.setValueAtTime(1, audioContext.currentTime);
        
        gainNode.gain.setValueAtTime(0, audioContext.currentTime);
        gainNode.gain.linearRampToValueAtTime(0.02, audioContext.currentTime + 0.01);
        gainNode.gain.linearRampToValueAtTime(0.015, audioContext.currentTime + 0.2);
        gainNode.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + 0.8);
        
        oscillator.connect(filter);
        filter.connect(gainNode);
        gainNode.connect(audioContext.destination);
        
        const now = audioContext.currentTime;
        oscillator.start(now + i * 0.05); // Slight stagger
        oscillator.stop(now + 0.8);
      });
    } catch {}
  };

  const playErrorSound = () => {
    try {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.frequency.setValueAtTime(220, audioContext.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(180, audioContext.currentTime + 0.15);
      oscillator.type = 'sawtooth';

      gainNode.gain.setValueAtTime(0, audioContext.currentTime);
      gainNode.gain.linearRampToValueAtTime(0.03, audioContext.currentTime + 0.01);
      gainNode.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + 0.25);

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      const now = audioContext.currentTime;
      oscillator.start(now);
      oscillator.stop(now + 0.25);
    } catch {}
  };

  const renderMultipleChoice = () => {
    return (
      <div className="question-options">
        {question.options?.map((option, index) => (
          <button
            key={index}
            className={`option-button ${selectedAnswer === index ? 'selected' : ''} ${isSubmitted ? 'disabled' : ''}`}
            onClick={() => handleMultipleChoiceSelect(index, option)}
            disabled={disabled || isSubmitted}
            style={{
              padding: '1rem 1.5rem',
              margin: '0.5rem 0',
              background: selectedAnswer === index 
                ? 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)'
                : 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
              border: selectedAnswer === index 
                ? '2px solid #FFD700' 
                : '2px solid rgba(0, 0, 0, 0.1)',
              borderRadius: '1rem',
              fontSize: '1rem',
              fontWeight: '600',
              color: selectedAnswer === index ? '#fff' : '#2C3E50',
              cursor: (disabled || isSubmitted) ? 'not-allowed' : 'pointer',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              transform: selectedAnswer === index ? 'scale(1.02)' : 'scale(1)',
              boxShadow: selectedAnswer === index 
                ? '0 8px 25px rgba(255, 215, 0, 0.4)' 
                : '0 2px 10px rgba(0, 0, 0, 0.1)',
              textShadow: selectedAnswer === index ? '0 1px 2px rgba(0, 0, 0, 0.3)' : 'none',
              fontFamily: 'Fredoka One, Arial, sans-serif',
              width: '100%',
              textAlign: 'left' as const,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              position: 'relative' as const,
              overflow: 'hidden'
            }}
            onMouseEnter={(e) => {
              if (!disabled && !isSubmitted && selectedAnswer !== index) {
                e.currentTarget.style.transform = 'scale(1.01)';
                e.currentTarget.style.boxShadow = '0 4px 15px rgba(0, 0, 0, 0.15)';
              }
            }}
            onMouseLeave={(e) => {
              if (!disabled && !isSubmitted && selectedAnswer !== index) {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.boxShadow = '0 2px 10px rgba(0, 0, 0, 0.1)';
              }
            }}
          >
            <span>{option}</span>
            {selectedAnswer === index && isSubmitted && (
              <span style={{ fontSize: '1.2rem', animation: 'bounce 0.6s ease-out' }}>
                ✨
              </span>
            )}
            
            {/* Selection ripple effect */}
            {selectedAnswer === index && isAnimating && (
              <div
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: '20px',
                  height: '20px',
                  background: 'rgba(255, 255, 255, 0.6)',
                  borderRadius: '50%',
                  animation: 'ripple 0.6s ease-out'
                }}
              />
            )}
          </button>
        ))}
      </div>
    );
  };

  const renderEmojiRating = () => {
    const blockAQ = question as BlockAQuestion;
    const { emojiRange } = blockAQ;
    
    if (!emojiRange) return null;
    
    return (
      <div className="emoji-rating">
        <div 
          style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '1rem',
            flexWrap: 'wrap',
            padding: '1rem 0'
          }}
        >
          {emojiRange.emojis.map((emoji, index) => {
            const value = index + 1;
            const isSelected = selectedAnswer === value;
            
            return (
              <button
                key={index}
                className={`emoji-button ${isSelected ? 'selected' : ''}`}
                onClick={() => handleEmojiSelect(value, emoji)}
                disabled={disabled || isSubmitted}
                style={{
                  fontSize: '3rem',
                  padding: '1rem',
                  background: isSelected 
                    ? 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)'
                    : 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                  border: isSelected 
                    ? '3px solid #FFD700' 
                    : '3px solid rgba(0, 0, 0, 0.1)',
                  borderRadius: '50%',
                  cursor: (disabled || isSubmitted) ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  transform: isSelected ? 'scale(1.2)' : 'scale(1)',
                  boxShadow: isSelected 
                    ? '0 10px 30px rgba(255, 215, 0, 0.5)' 
                    : '0 4px 15px rgba(0, 0, 0, 0.1)',
                  width: '80px',
                  height: '80px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative' as const,
                  overflow: 'hidden'
                }}
                onMouseEnter={(e) => {
                  if (!disabled && !isSubmitted && !isSelected) {
                    e.currentTarget.style.transform = 'scale(1.1)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!disabled && !isSubmitted && !isSelected) {
                    e.currentTarget.style.transform = 'scale(1)';
                  }
                }}
              >
                {emoji}
                
                {isSelected && isAnimating && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      width: '10px',
                      height: '10px',
                      background: 'rgba(255, 255, 255, 0.8)',
                      borderRadius: '50%',
                      animation: 'ripple 0.6s ease-out'
                    }}
                  />
                )}
              </button>
            );
          })}
        </div>
        
        {/* Rating labels */}
        <div 
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            padding: '0 2rem',
            fontSize: '0.9rem',
            fontWeight: '600',
            color: '#546E7A'
          }}
        >
          <span>Not at all</span>
          <span>Loved it!</span>
        </div>
      </div>
    );
  };

  const renderTextInput = () => {
    return (
      <div className="text-input-container">
        <textarea
          value={textAnswer}
          onChange={(e) => setTextAnswer(e.target.value)}
          disabled={disabled || isSubmitted}
          placeholder="Type your answer here..."
          maxLength={500}
          style={{
            width: '100%',
            minHeight: '120px',
            padding: '1rem',
            border: '2px solid rgba(0, 0, 0, 0.1)',
            borderRadius: '1rem',
            fontSize: '1rem',
            fontFamily: 'inherit',
            resize: 'vertical',
            background: 'linear-gradient(135deg, #f8f9fa 0%, #fff 100%)',
            color: '#2C3E50',
            outline: 'none',
            transition: 'all 0.3s ease'
          }}
          onFocus={(e) => {
            e.currentTarget.style.border = '2px solid #4FC3F7';
            e.currentTarget.style.boxShadow = '0 4px 15px rgba(79, 195, 247, 0.2)';
          }}
          onBlur={(e) => {
            e.currentTarget.style.border = '2px solid rgba(0, 0, 0, 0.1)';
            e.currentTarget.style.boxShadow = 'none';
          }}
        />
        
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.5rem' }}>
          <span style={{ fontSize: '0.8rem', color: '#9E9E9E' }}>
            {textAnswer.length}/500 characters
          </span>
          <button
            onClick={handleTextSubmit}
            disabled={disabled || isSubmitted || !textAnswer.trim()}
            style={{
              padding: '0.5rem 1.5rem',
              background: textAnswer.trim() 
                ? 'linear-gradient(135deg, #4FC3F7 0%, #45B7D1 100%)'
                : 'linear-gradient(135deg, #e9ecef 0%, #dee2e6 100%)',
              color: textAnswer.trim() ? '#fff' : '#6c757d',
              border: 'none',
              borderRadius: '0.5rem',
              fontSize: '0.9rem',
              fontWeight: '600',
              cursor: (disabled || isSubmitted || !textAnswer.trim()) ? 'not-allowed' : 'pointer',
              transition: 'all 0.3s ease',
              fontFamily: 'inherit'
            }}
          >
            Submit Answer
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="question-card">
      <div
        style={{
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.9) 100%)',
          borderRadius: '1.5rem',
          padding: '2rem',
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          backdropFilter: 'blur(10px)',
          position: 'relative',
          overflow: 'hidden',
          transition: 'all 0.3s ease',
          transform: isAnimating ? 'scale(1.02)' : 'scale(1)'
        }}
      >
        {/* Question Text */}
        <div
          style={{
            fontSize: '1.3rem',
            fontWeight: 'bold',
            color: '#2C3E50',
            marginBottom: '2rem',
            textAlign: 'center',
            lineHeight: '1.4',
            fontFamily: 'Fredoka One, Arial, sans-serif',
            whiteSpace: 'pre-line'
          }}
        >
          {question.prompt}
        </div>
        
        {/* Question Type Rendering */}
        {question.type === 'multiple_choice' && renderMultipleChoice()}
        {isBlockA && (question as BlockAQuestion).type === 'emoji_rating' && renderEmojiRating()}
        {isCampaign && question.type === 'text' && renderTextInput()}
        
        {/* Submit Success Indicator */}
        {isSubmitted && (
          <div
            style={{
              position: 'absolute',
              top: '1rem',
              right: '1rem',
              fontSize: '2rem',
              animation: 'bounceIn 0.6s ease-out'
            }}
          >
            ✅
          </div>
        )}
        
        {/* Background decorations */}
        <div
          style={{
            position: 'absolute',
            top: '-50%',
            left: '-50%',
            width: '200%',
            height: '200%',
            background: 'radial-gradient(circle, rgba(79, 195, 247, 0.05) 0%, transparent 70%)',
            pointerEvents: 'none',
            animation: isAnimating ? 'rotate 2s linear' : 'none'
          }}
        />
      </div>
      
      
      {/* CSS Animations */}
      <style>{`
        @keyframes bounce {
          0%, 20%, 60%, 100% {
            transform: translateY(0);
          }
          40% {
            transform: translateY(-10px);
          }
          80% {
            transform: translateY(-5px);
          }
        }
        
        @keyframes ripple {
          0% {
            width: 20px;
            height: 20px;
            opacity: 1;
          }
          100% {
            width: 200px;
            height: 200px;
            opacity: 0;
          }
        }
        
        @keyframes bounceIn {
          0% {
            opacity: 0;
            transform: scale(0.3) rotate(-180deg);
          }
          50% {
            opacity: 1;
            transform: scale(1.05) rotate(-90deg);
          }
          70% {
            transform: scale(0.9) rotate(-45deg);
          }
          100% {
            opacity: 1;
            transform: scale(1) rotate(0deg);
          }
        }
        
        @keyframes rotate {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </div>
  );
};

export default QuestionCard;